import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import Animated, { FadeIn, ZoomIn, ZoomOut } from 'react-native-reanimated';

import { Box, HStack, Heading, Text } from '@squire/ui';

import { InfoCircleIcon } from '@/components/_icons';
import type { Service } from '@/types/booking';
import { isAddonService } from '@/utils/isAddonService';

import {
  CategorySelect,
  UNCATEGORIZED_CATEGORY_ID,
} from '../CategorySelect/CategorySelect';
import { ServiceCard } from '../ServiceCard/ServiceCard';
import { ServiceGridSkeleton } from '../ServiceGridSkeleton';
import { styles } from './styles';
import type { ServiceGridProps } from './types';

const AnimatedBox = Animated.createAnimatedComponent(Box);

export function ServiceGrid({
  services,
  categories,
  selectedCategoryId,
  selectedServiceId,
  onServiceSelect,
  onCategoryChange,
  isLoading = false,
  error = null,
  isSelecting: _isSelecting = false,
  additionalServicesIds,
  testID = 'service-grid',
  style,
  columns = 2,
  allowMultipleServices = true,
  currency,
  giftCardEnabled,
  onGiftCardPress,
  onPrimaryRemoveWithAddons,
  ...props
}: ServiceGridProps) {
  const { t } = useTranslation('common', { keyPrefix: 'ServiceGrid' });

  // The gift card item's entrance fade-in is meant for the initial cascade
  // when the grid first loads with nothing selected. Once the shopper has
  // selected a service at least once, the gift card reappearing (e.g. after
  // deselecting) is a direct result of that interaction, not a fresh page
  // load, so it should render immediately instead of waiting out a delay
  // based on its position.
  const hasSelectedServiceRef = useRef(!!selectedServiceId);
  useEffect(() => {
    if (selectedServiceId) {
      hasSelectedServiceRef.current = true;
    }
  }, [selectedServiceId]);

  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      if (service.addonOnly) return false;
      // Non-standalone services (0 min or $0, including ranges starting at 0)
      // are add-ons: they only make sense as secondary selections on top of a
      // primary. In single-select shops/barbers there is no add-on concept and
      // tapping a service replaces the primary, so hide them entirely. In
      // multi-select, hide them only until a primary is selected (then they
      // reappear as add-on options).
      if (
        isAddonService(service) &&
        (!allowMultipleServices || !selectedServiceId)
      )
        return false;
      if (selectedCategoryId === UNCATEGORIZED_CATEGORY_ID)
        return !service.categories?.length;
      if (selectedCategoryId)
        return service.categories?.some((cat) => cat.id === selectedCategoryId);
      return true;
    });
  }, [services, selectedCategoryId, selectedServiceId, allowMultipleServices]);

  const selectedService = useMemo(() => {
    if (!selectedServiceId) return null;
    return (
      services.find(
        (service) =>
          service.id === selectedServiceId ||
          service.parentId === selectedServiceId,
      ) || null
    );
  }, [services, selectedServiceId]);

  const gridServices = useMemo(() => {
    if (!selectedService) return filteredServices;
    // For single-service barbers, keep the service in the grid (highlight inline instead of elevating)
    if (!allowMultipleServices) return filteredServices;
    return filteredServices.filter(
      (service) =>
        service.id !== selectedServiceId &&
        service.parentId !== selectedServiceId,
    );
  }, [
    filteredServices,
    selectedService,
    selectedServiceId,
    allowMultipleServices,
  ]);

  const addonSectionServices = useMemo(() => {
    if (!additionalServicesIds?.length) return [];
    return additionalServicesIds
      .map((id) => services.find((s) => s.id === id || s.parentId === id))
      .filter((s): s is Service => !!s && isAddonService(s));
  }, [additionalServicesIds, services]);

  const handleSelectedServicePress = useCallback(
    (service: Service) => {
      if (addonSectionServices.length > 0) {
        onPrimaryRemoveWithAddons?.();
      } else {
        onServiceSelect(service);
      }
    },
    [addonSectionServices, onPrimaryRemoveWithAddons, onServiceSelect],
  );

  if (isLoading) {
    return (
      <ServiceGridSkeleton columns={columns} testID={`${testID}-loading`} />
    );
  }

  // Error state
  if (error) {
    return (
      <View
        style={[styles.container, style]}
        testID={`${testID}-error`}
        {...props}
      >
        <View style={styles.errorContainer}>
          <InfoCircleIcon
            size={64}
            color="iconNegative"
            style={styles.errorIcon}
          />
          <Heading variant="s" style={styles.errorTitle}>
            {t('error.title')}
          </Heading>
          <Text variant="m" style={styles.errorMessage}>
            {t('error.message')}
          </Text>
        </View>
      </View>
    );
  }

  // Empty state
  const renderEmptyState = () => {
    const isFiltered = selectedCategoryId !== undefined;
    const title = isFiltered
      ? t('empty.filtered.title')
      : t('empty.unfiltered.title');
    const message = isFiltered
      ? t('empty.filtered.message')
      : t('empty.unfiltered.message');

    return (
      <View style={styles.emptyStateContainer}>
        <InfoCircleIcon
          size={64}
          color="iconTertiary"
          style={styles.emptyStateIcon}
        />
        <Heading variant="s" style={styles.emptyStateTitle}>
          {title}
        </Heading>
        <Text variant="m" style={styles.emptyStateMessage}>
          {message}
        </Text>
      </View>
    );
  };

  return (
    <View style={[styles.container, style]} testID={testID} {...props}>
      <View style={[styles.gridSection]}>
        <Animated.ScrollView
          bounces={false}
          testID={`${testID}-list`}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[
            styles.scrollViewContentContainer,
            selectedServiceId && styles.viewOrderBarPadding,
          ]}
        >
          {/* Selected Service Section (elevated above category filter) - skip for single-service barbers */}
          {selectedService && allowMultipleServices && (
            <AnimatedBox
              style={[styles.gridItem(columns), styles.selectedSection]}
              entering={ZoomIn.duration(200)}
              exiting={ZoomOut.duration(200)}
            >
              <ServiceCard
                selected="main"
                service={selectedService}
                style={styles.gridItem(columns)}
                testID={`service-card-shadow-${selectedService.id}`}
                onPress={() => handleSelectedServicePress(selectedService)}
                currency={currency}
              />
            </AnimatedBox>
          )}

          {selectedService && allowMultipleServices && (
            <View style={styles.additionalServicesTitle}>
              <Heading variant="s">{t('additionalServicesTitle')}</Heading>
            </View>
          )}

          {/* Category Filter Section */}
          {categories.length > 0 && (
            <View style={styles.categorySection}>
              <CategorySelect
                categories={categories}
                selectedCategoryId={selectedCategoryId}
                onCategoryChange={onCategoryChange}
                placeholder={t('categoryFilterPlaceholder')}
                testID={`${testID}-category-select`}
              />
            </View>
          )}

          {!gridServices.length &&
            !selectedServiceId &&
            !giftCardEnabled &&
            renderEmptyState()}

          <HStack
            style={styles.servicesGrid}
            testID={`${testID}-${columns}-column`}
          >
            {gridServices.map((service, index) => {
              const isSelectedInline = allowMultipleServices
                ? false
                : !!selectedServiceId &&
                  (service.id === selectedServiceId ||
                    service.parentId === selectedServiceId);

              return (
                <Animated.View
                  key={service.id}
                  entering={FadeIn.delay(50 * index)}
                >
                  <Box
                    testID={`service-card-container-${index}`}
                    style={styles.gridItem(columns)}
                    key={service.id}
                  >
                    <ServiceCard
                      service={service}
                      selected={
                        isSelectedInline
                          ? 'main'
                          : additionalServicesIds?.find(
                                (id) =>
                                  id === service.id || service.parentId === id,
                              )
                            ? 'additional'
                            : undefined
                      }
                      onPress={() => onServiceSelect(service)}
                      testID={`service-card-${index}`}
                      currency={currency}
                    />
                  </Box>
                </Animated.View>
              );
            })}
            {giftCardEnabled && (
              <Animated.View
                entering={
                  hasSelectedServiceRef.current
                    ? undefined
                    : FadeIn.delay(50 * gridServices.length)
                }
              >
                <Box
                  testID="service-card-container-gift"
                  style={styles.gridItem(columns)}
                >
                  <ServiceCard
                    mode="giftCard"
                    currency={currency}
                    onPress={onGiftCardPress}
                    testID="gift-card-service-grid-item"
                    style={styles.gridItem(columns)}
                  />
                </Box>
              </Animated.View>
            )}
          </HStack>
        </Animated.ScrollView>
      </View>
    </View>
  );
}

ServiceGrid.displayName = 'ServiceGrid';
